import React, { useState, useEffect } from 'react';
import { Activity, Map as MapIcon, BarChart2 } from 'lucide-react';
import { MapContainer, TileLayer, CircleMarker, Tooltip, Popup, LayersControl, LayerGroup, useMap, useMapEvents } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const MapBounds = ({ sites }) => {
    const map = useMap();
    useEffect(() => {
        if (sites && sites.length > 0) {
            const validSites = sites.filter(s => s.latitude && s.longitude);
            if (validSites.length > 0) {
                const bounds = L.latLngBounds(validSites.map(s => [s.latitude, s.longitude]));
                if (bounds.isValid()) {
                    map.flyToBounds(bounds, { padding: [50, 50], maxZoom: 12, duration: 1.5 });
                }
            }
        }
    }, [sites, map]);
    useEffect(() => {
        const timer = setTimeout(() => {
            if (map) map.invalidateSize();
        }, 150);
        return () => clearTimeout(timer);
    }, [map]);

    return null;
};

const MapEvents = () => {
    const map = useMapEvents({
        baselayerchange: (e) => {
            if (e.name.includes('Satellite') || e.name === 'Dark') {
                map.getContainer().classList.add('satellite-mode');
            } else {
                map.getContainer().classList.remove('satellite-mode');
            }
        }
    });
    return null;
};

const createCustomClusterIcon = (cluster) => {
    const childCount = cluster.getChildCount();
    const markers = cluster.getAllChildMarkers();
    
    // Use the color of the first marker for the single dot
    const color = markers[0]?.options?.siteData?.computedColor || '#76AB3F';
    
    return L.divIcon({
        html: `
            <div style="position: relative; display: flex; justify-content: center; align-items: center; width: 100%; height: 100%;">
                <div style="width: 8px; height: 8px; border-radius: 50%; background-color: ${color}; border: 1.5px solid #ffffff; box-shadow: 0 1px 3px rgba(0,0,0,0.3);"></div>
                <div style="position: absolute; top: 0px; right: 0px; background: #ffffff; border: 1px solid #e5e7eb; border-radius: 10px; padding: 1px 5px; font-size: 10px; font-weight: 800; color: #374151; box-shadow: 0 1px 2px rgba(0,0,0,0.15); line-height: 1.2; transform: translate(25%, -25%);">
                    ${childCount}
                </div>
            </div>
        `,
        className: 'marker-cluster-transparent',
        iconSize: L.point(30, 30, true),
    });
};

const SiteIntelligence = ({ selectedClient, clientColorMap = {} }) => {
    const [data, setData] = useState({
        summary: { total_sites: 0, total_clusters: 0, largest_cluster: 0, largest_cluster_name: '' },
        radius_summary: { "10km": 0, "25km": 0, "50km": 0 },
        clusters: [],
        sites: []
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState('dashboard'); // 'dashboard' or 'map'
    const [clusterPopup, setClusterPopup] = useState(null);

    const handleClusterClick = (cluster) => {
        const markers = cluster.getAllChildMarkers();
        const sites = markers.map(m => m.options.siteData).filter(Boolean);
        const latlng = cluster.getLatLng();
        setClusterPopup({
            position: [latlng.lat, latlng.lng],
            sites: sites
        });
    };

    const fetchIntelligence = async () => {
        setLoading(true);
        setError(null);
        setClusterPopup(null); // Clear any open popups when client changes
        try {
            const clientQuery = selectedClient !== 'All Clients' ? `?clientName=${encodeURIComponent(selectedClient)}` : '';
            const response = await fetch(`/api/site-intelligence${clientQuery}`);
            if (!response.ok) throw new Error('Failed to fetch site intelligence data');
            const json = await response.json();
            setData(json);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchIntelligence();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedClient]);

    return (
        <div className="flex flex-col h-full w-full bg-gray-50 overflow-hidden">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-8 py-6 flex items-center justify-between shrink-0">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                        Site intelligence
                    </h1>
                    <p className="text-gray-500 text-sm mt-1">Spatial analytics and clustering for {selectedClient}</p>
                </div>
                <div className="flex bg-gray-100 p-1 rounded-lg">
                    <button 
                        onClick={() => setViewMode('dashboard')}
                        className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${viewMode === 'dashboard' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        <BarChart2 size={16} /> Dashboard
                    </button>
                    <button 
                        onClick={() => setViewMode('map')}
                        className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${viewMode === 'map' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        <MapIcon size={16} /> Map View
                    </button>
                </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-auto p-8">
                {error && (
                    <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6 border border-red-200">
                        {error}
                    </div>
                )}

                {loading ? (
                    <div className="flex justify-center items-center h-64 text-[#76AB3F]">
                        <Activity className="animate-spin" size={40} />
                    </div>
                ) : viewMode === 'dashboard' ? (
                    <div className="w-full space-y-6">
                        {/* Top Stats Cards */}
                        <div className="grid grid-cols-2 md:flex md:flex-wrap gap-4 md:gap-6">
                            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center gap-4 w-full md:flex-1 md:min-w-[160px] md:max-w-[220px]">
                                <div>
                                    <p className="text-sm text-gray-500 font-medium">Total Sites</p>
                                    <p className="text-xl font-bold text-gray-800">{data.summary.total_sites}</p>
                                </div>
                            </div>
                            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center gap-4 w-full md:flex-1 md:min-w-[160px] md:max-w-[220px]">
                                <div>
                                    <p className="text-sm text-gray-500 font-medium">Unique Regions</p>
                                    <p className="text-xl font-bold text-gray-800">{data.summary.total_clusters}</p>
                                </div>
                            </div>
                            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center gap-4 w-full md:flex-1 md:min-w-[160px] md:max-w-[220px]">
                                <div>
                                    <p className="text-sm text-gray-500 font-medium">Largest Region</p>
                                    <p className="text-xl font-bold text-gray-800">{data.summary.largest_cluster}</p>
                                </div>
                            </div>
                            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center gap-4 w-full md:flex-1 md:min-w-[160px] md:max-w-[220px]">
                                <div>
                                    <p className="text-sm text-gray-500 font-medium">Density (Sites &lt; 50km)</p>
                                    <p className="text-xl font-bold text-gray-800">{data.radius_summary["50km"]}</p>
                                </div>
                            </div>
                        </div>

                        {/* Main Charts / Details Area */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            
                            {/* Proximity Analysis */}
                            <div className="lg:col-span-1 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
                                    Proximity Analysis
                                </h3>
                                <p className="text-sm text-gray-500 mb-6">
                                    Number of sites that have at least one other site within the specific distance. High numbers indicate dense clustering.
                                </p>
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg border border-gray-100">
                                        <span className="font-semibold text-gray-700">Within 10 km</span>
                                        <span className="bg-white px-3 py-1 rounded-md text-sm font-bold text-[#76AB3F] border border-gray-200">{data.radius_summary["10km"]} sites</span>
                                    </div>
                                    <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg border border-gray-100">
                                        <span className="font-semibold text-gray-700">Within 25 km</span>
                                        <span className="bg-white px-3 py-1 rounded-md text-sm font-bold text-blue-500 border border-gray-200">{data.radius_summary["25km"]} sites</span>
                                    </div>
                                    <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg border border-gray-100">
                                        <span className="font-semibold text-gray-700">Within 50 km</span>
                                        <span className="bg-white px-3 py-1 rounded-md text-sm font-bold text-indigo-500 border border-gray-200">{data.radius_summary["50km"]} sites</span>
                                    </div>
                                </div>
                            </div>

                            {/* Top Clusters */}
                            <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex flex-col">
                                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                                    Top Regions / Postal Codes
                                </h3>
                                <div className="overflow-auto flex-1 border border-gray-100 rounded-lg">
                                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left font-semibold text-gray-600 uppercase tracking-wider">Region / Zip</th>
                                                <th className="px-6 py-3 text-left font-semibold text-gray-600 uppercase tracking-wider">Site Count</th>
                                                <th className="px-6 py-3 text-left font-semibold text-gray-600 uppercase tracking-wider">% of Total</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-100">
                                            {data.clusters.slice(0, 10).map((cluster, i) => (
                                                <tr key={i} className="hover:bg-gray-50">
                                                    <td className="px-6 py-3 whitespace-nowrap font-medium text-gray-800">{cluster.name}</td>
                                                    <td className="px-6 py-3 whitespace-nowrap text-gray-600 font-semibold">{cluster.count}</td>
                                                    <td className="px-6 py-3 whitespace-nowrap">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-gray-500 w-10">{((cluster.count / data.summary.total_sites) * 100).toFixed(1)}%</span>
                                                            <div className="w-24 bg-gray-200 rounded-full h-1.5">
                                                                <div className="bg-[#76AB3F] h-1.5 rounded-full" style={{ width: `${(cluster.count / data.summary.total_sites) * 100}%` }}></div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                            {data.clusters.length === 0 && (
                                                <tr>
                                                    <td colSpan="3" className="px-6 py-8 text-center text-gray-400">No regional data available.</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden h-full flex flex-col min-h-[500px]">
                        <MapContainer 
                            center={[39.8283, -98.5795]} 
                            zoom={4} 
                            minZoom={2}
                            maxBounds={[[-90, -180], [90, 180]]}
                            maxBoundsViscosity={1.0}
                            style={{ height: '100%', width: '100%' }} 
                            attributionControl={false}
                        >
                            <MapBounds sites={data.sites} />
                            <MapEvents />
                            <LayersControl position="bottomright">
                                <LayersControl.BaseLayer name="Satellite">
                                    <LayerGroup>
                                        <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
                                        <TileLayer url="https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}" opacity={1} />
                                    </LayerGroup>
                                </LayersControl.BaseLayer>
                                <LayersControl.BaseLayer name="Satellite (Old)">
                                    <LayerGroup>
                                        <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
                                        <TileLayer url="https://{s}.basemaps.cartocdn.com/rastertiles/light_only_labels/{z}/{x}/{y}{r}.png" />
                                    </LayerGroup>
                                </LayersControl.BaseLayer>
                                <LayersControl.BaseLayer checked name="OpenStreetMap">
                                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" noWrap={true} />
                                </LayersControl.BaseLayer>
                                <LayersControl.BaseLayer name="Light">
                                    <TileLayer url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" noWrap={true} />
                                </LayersControl.BaseLayer>
                                <LayersControl.BaseLayer name="Terrain">
                                    <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}" />
                                </LayersControl.BaseLayer>
                            </LayersControl>
                            {clusterPopup && (
                                <Popup 
                                    position={clusterPopup.position} 
                                    onClose={() => setClusterPopup(null)}
                                    className="site-detail-popup"
                                >
                                    <div style={{ fontFamily: "'Inter', sans-serif", width: "300px", padding: "16px", background: "#fff", borderRadius: "12px", boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)", margin: "-14px" }}>
                                        <div style={{ height: "4px", background: "linear-gradient(90deg, #76AB3F, #2563EB)", position: "absolute", top: 0, left: 0, right: 0 }}></div>
                                        <div style={{ fontSize: "16px", fontWeight: 700, color: "#1a2e0a", marginBottom: "12px", borderBottom: "1px solid #e5e7eb", paddingBottom: "8px", marginTop: "4px" }}>
                                            Sites in Region ({clusterPopup.sites.length})
                                        </div>
                                        <div style={{ maxHeight: "250px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "8px" }} className="custom-scrollbar">
                                            {clusterPopup.sites.map((site, idx) => (
                                                <div key={idx} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px", background: "#f9fafb", borderRadius: "8px", border: "1px solid #f3f4f6" }}>
                                                    <div style={{ width: "10px", height: "10px", borderRadius: "50%", background: site.computedColor || '#76AB3F', flexShrink: 0 }}></div>
                                                    <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
                                                        <span style={{ fontSize: "13px", fontWeight: 700, color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{site.site_name || 'Site'}</span>
                                                        <span style={{ fontSize: "11px", fontWeight: 600, color: "#76AB3F", letterSpacing: "0.02em" }}>{site.client_name || 'Client'}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </Popup>
                            )}

                            <MarkerClusterGroup 
                                chunkedLoading 
                                maxClusterRadius={50}
                                iconCreateFunction={createCustomClusterIcon}
                                zoomToBoundsOnClick={false}
                                showCoverageOnHover={false}
                                onClick={(e) => handleClusterClick(e.layer)}
                            >
                                {data.sites.map(site => {
                                    if (!site.latitude || !site.longitude) return null;
                                    const clientNameKey = (site.client_name || 'General').trim().toUpperCase();
                                    const clientColor = clientColorMap[clientNameKey] || site.Color || site.color || '#76AB3F';
                                    return (
                                        <CircleMarker 
                                            key={site.id} 
                                            center={[site.latitude, site.longitude]}
                                            radius={4}
                                            fillColor={clientColor}
                                            color="#ffffff"
                                            weight={1.5}
                                            fillOpacity={0.85}
                                            siteData={{ ...site, computedColor: clientColor }}
                                        >
                                            <Tooltip direction="top" offset={[0, -10]} opacity={1} className="site-hover-tooltip" style={{ padding: 0, border: 'none', background: 'transparent', boxShadow: 'none' }}>
                                                <div style={{ fontFamily: "'Inter', sans-serif", minWidth: "220px", maxWidth: "260px", overflow: "hidden", borderRadius: "12px", background: "#fff", margin: "-6px", boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)" }}>
                                                    <div style={{ height: "3px", background: "linear-gradient(90deg, #76AB3F, #2563EB)" }}></div>
                                                    <div style={{ padding: "12px 14px" }}>
                                                        <div style={{ fontSize: "14px", fontWeight: 700, color: "#1a2e0a", marginBottom: "8px", lineHeight: 1.3, wordBreak: "break-word" }}>
                                                            {site.site_name || 'Site'}
                                                        </div>
                                                        
                                                        <div style={{ display: "inline-flex", alignItems: "center", gap: "5px", background: "#EAF4DB", border: "1px solid #C7E0A8", borderRadius: "20px", padding: "2px 9px", marginBottom: "10px" }}>
                                                            <div style={{ width: "5px", height: "5px", borderRadius: "50%", background: "#76AB3F" }}></div>
                                                            <span style={{ fontSize: "10px", fontWeight: 600, color: "#4a7a28", letterSpacing: "0.04em" }}>{site.client_name || 'Client'}</span>
                                                        </div>

                                                        <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                                                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
                                                                <span style={{ fontSize: "11px", color: "#9ca3af", fontWeight: 500 }}>Coordinates</span>
                                                                <span style={{ fontSize: "11px", color: "#374151", fontFamily: "monospace", fontWeight: 600 }}>
                                                                    {site.latitude != null ? site.latitude.toFixed(4) : '-'}, {site.longitude != null ? site.longitude.toFixed(4) : '-'}
                                                                </span>
                                                            </div>
                                                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
                                                                <span style={{ fontSize: "11px", color: "#9ca3af", fontWeight: 500, flexShrink: 0 }}>Address</span>
                                                                <span style={{ fontSize: "11px", color: "#374151", textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "150px" }} title={site.address || site.postal_code || '-'}>
                                                                    {site.address || site.postal_code || '-'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Tooltip>
                                        </CircleMarker>
                                    );
                                })}
                            </MarkerClusterGroup>
                        </MapContainer>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SiteIntelligence;
